import api from './api'

export type DealBookAction = 'create' | 'exists' | 'skip' | 'reject'

export interface DealBookDeal {
  deal: string
  rows: string[]
  action: DealBookAction
  stage: string | null
  reasons: string[]
  /** Something the file states that is left blank or settled by a rule: worth a look before committing. */
  warnings: string[]
  /** How the file was read, correctly and as designed ("B. Collyer" read as Brian Collyer): not a problem. */
  notes?: string[]
  /** The field each warning is about, so the preview marks the cell it concerns. */
  warning_fields?: { field: string; warning: string }[]
  funded_on?: string | null
  /** The day the deal entered its Backshop status (Current Loan Status Date). */
  status_on?: string | null
  /** Datapoints the file does not state: the deal is created with them blank and opens in the Data Intelligence Review. */
  gaps?: { field: string; label: string; why: string }[]
  /** {column id: the cell as the Pipeline grid prints it} once the deal is created; for a deal already in
   *  Helios, what the file states. Null for a deal rejected before its row was read. */
  cells?: Record<string, string> | null
  /** {column id: why} for each cell the create path fills with a platform default, not from the file. */
  defaults?: Record<string, string>
}

/** One preview column: the Pipeline report's header, and the plan fields that land in it (a gap's or a
 *  warning's `field`), so a problem marks the cell it concerns. */
export interface DealBookColumn {
  id: string
  header: string
  fields: string[]
}

export interface DealBookResult {
  file: string | null
  sha256: string
  partition: string
  committed: boolean
  counts: Record<DealBookAction, number>
  /** How many of the deals to create open in the Data Intelligence Review for a datapoint the file does not state. */
  needs_review?: number
  /** The preview grid's columns, in order; the first is the deal. */
  columns?: DealBookColumn[]
  deals: DealBookDeal[]
  not_imported_columns: Record<string, string>
  preview_token?: string
  created?: { deal: string; loan_id: string; warning?: string }[]
  failed?: { deal: string; reason: string }[]
}

function form(files: File[], commit: boolean, previewToken?: string): FormData {
  const body = new FormData()
  // One workbook with both sheets, or the Status and Pipeline reports as two files: the same field, repeated.
  for (const file of files) body.append('file', file)
  body.append('commit', String(commit))
  if (previewToken) body.append('preview_token', previewToken)
  return body
}

/** A preview that has not answered in this long is not coming: say so rather than spin. */
export const PREVIEW_TIMEOUT_MS = 180_000

/** POST /deal-book-import: preview by default; commit needs the preview's token. */
export const dealBookImportService = {
  async preview(files: File[]): Promise<DealBookResult> {
    // Reading writes nothing, so giving up on it is safe. The commit has no
    // timeout: deals may still be being created, and a retry would not help.
    const { data } = await api.post<DealBookResult>('/deal-book-import', form(files, false), {
      timeout: PREVIEW_TIMEOUT_MS,
    })
    return data
  },
  async commit(files: File[], previewToken: string): Promise<DealBookResult> {
    const { data } = await api.post<DealBookResult>('/deal-book-import', form(files, true, previewToken))
    return data
  },
}
